import type { ProductSummary } from './types'

/**
 * Up to this many branches, the Branches tab is one grid — a column per branch —
 * rather than one branch at a time.
 *
 * Six columns of checkboxes still fit beside a product name on a laptop; past
 * that the grid scrolls sideways, and a merchant comparing the first column
 * against the ninth has lost the point of seeing them together. Larger chains
 * get the single-branch view and the picker in the module bar instead.
 */
export const BRANCH_GRID_MAX = 6

/**
 * The two ways the Branches tab reads the same links.
 *
 * `Product` is one product at a time — pick it, tick its branches, save — and
 * works for any number of branches. `Branch` is every branch at once: the grid
 * up to `BRANCH_GRID_MAX`, one branch at a time past it.
 */
export const BranchView = {
  Product: 'product',
  Branch: 'branch',
} as const

export type BranchView = (typeof BranchView)[keyof typeof BranchView]

/** Same page as the Products tab, so moving between the two keeps the same rows in view. */
export const BRANCH_PAGE_SIZE = 10

/** How long the merchant has to stop typing before the list is asked again. */
export const BRANCH_SEARCH_DEBOUNCE_MS = 300

/**
 * Whether the list sent this product's outlets at all.
 *
 * A row without them is not known either way — reading it as "sold nowhere"
 * would offer to add a product that is already there — so its controls stay
 * shut and the page-wide actions leave it out.
 */
export function isBranchKnown(product: ProductSummary): boolean {
  return product.branches !== undefined
}

export function isSoldAt(product: ProductSummary, branchId: string): boolean {
  return Boolean(product.branches?.some((branch) => branch.id === branchId))
}

/**
 * This branch is the only one the product has. Taking it away would leave the
 * product on no till, which the server refuses — so the control is locked
 * instead of offered and then rejected.
 */
export function isOnlyBranchOf(product: ProductSummary, branchId: string): boolean {
  return isSoldAt(product, branchId) && product.branches?.length === 1
}
